import React, { useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '.././contexts/AuthContext';

const AdminSideMenu = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isSpecialPackagesOpen, setIsSpecialPackagesOpen] = useState(false);
    const { user, userRole, logout } = useAuth();
    const navigate = useNavigate(); // Add this

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };
    const toggleSpecialPackagesMenu = () => {
        setIsSpecialPackagesOpen(!isSpecialPackagesOpen);
    };
    // Helper function to check if menu item should be shown
    const canViewMenuItem = (requiredRole) => {
        // SUPER_ADMIN can see everything
        if (userRole === 'SUPER_ADMIN') {
            return true;
        }

        // Define which roles can see which menu items
        const menuPermissions = {
            adminControl: ['SUPER_ADMIN'],
            carrierApproval: ['SUPER_ADMIN', 'MAIN_ADMIN'],
            driverApproval: ['SUPER_ADMIN', 'MAIN_ADMIN'],
            profileUpdate: ['SUPER_ADMIN', 'MAIN_ADMIN'],
            carriers: ['SUPER_ADMIN', 'MAIN_ADMIN', 'MARKETING_ADMIN'],
            packages: ['SUPER_ADMIN', 'MAIN_ADMIN'],
            specialPackages: ['SUPER_ADMIN', 'MAIN_ADMIN'],
            drivers: ['SUPER_ADMIN', 'MAIN_ADMIN'],
            demoSchedule: ['SUPER_ADMIN', 'MAIN_ADMIN', 'MARKETING_ADMIN'],
            driverAssign: ['SUPER_ADMIN', 'MAIN_ADMIN'],
            addDriver: ['SUPER_ADMIN', 'MAIN_ADMIN'],
            revenue: ['SUPER_ADMIN', 'MAIN_ADMIN'],
            reportedIssues: ['SUPER_ADMIN', 'MAIN_ADMIN', 'SUPPORT_ADMIN'],
            reports: ['SUPER_ADMIN', 'MAIN_ADMIN', 'MARKETING_ADMIN', 'ACCOUNTS_ADMIN'],
        };

        return menuPermissions[requiredRole]?.includes(userRole) || false;
    };

    // Updated handleLogout function
    const handleLogout = () => {
        logout();
        navigate('/'); // Navigate to base route
    };

    // Get user initials for avatar
    const getUserInitials = () => {
        const name = user?.name || 'User';
        return name
            .split(' ')
            .map((word) => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Add this function near the top of your component
    const capitalizeFirstLetter = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    const formatRole = (role) => {
        if (!role) return '';
        return role
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' '); // Use space instead of underscore
    };

    return (
        <>
            {/* ✅ Mobile Top Header with Hamburger Button (fixed, z-50) */}
            <div className='md:hidden fixed top-0 left-0 w-full bg-black text-white flex items-center justify-between px-6 py-4 z-50 shadow-md'>
                <div className='text-blue-600 text-2xl font-bold'>
                    <img src='../assets/FleetyBlue.png' alt='Fleety Logo' className='h-10' />
                </div>
                <button onClick={toggleMobileMenu} className='text-white text-2xl focus:outline-none'>
                    <i className='fas fa-bars'></i>
                </button>
            </div>

            <div className='md:hidden h-20'></div>

            {/* Desktop Sidebar */}
            <aside className='fixed top-0 left-0 h-screen w-64 bg-black text-white flex flex-col z-50 overflow-y-auto hidden md:flex'>
                <div className='p-6 text-3xl font-bold text-blue-700 border-b border-gray-800'>
                    <img src='../assets/FleetyBlue.png' alt='Fleety Logo' className='h-12' />
                </div>
                <nav className='flex-1 px-4 py-6 space-y-2'>
                    {/* Dashboard - Always Visible */}
                    <NavLink
                        to='/admin/dashboard'
                        className={({ isActive }) =>
                            `block py-3 px-4 rounded-lg font-medium flex items-center text-white ${
                                isActive ? 'bg-blue-700' : 'hover:bg-blue-600'
                            } transition duration-200`
                        }
                    >
                        <svg
                            className='w-5 h-5 mr-3'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                            xmlns='http://www.w3.org/2000/svg'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                d='M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z'
                            />
                        </svg>
                        Admin Dashboard
                    </NavLink>

                    {/* Admin Control - Only SUPER_ADMIN and MAIN_ADMIN */}
                    {canViewMenuItem('adminControl') && (
                        <NavLink
                            to='/admin/adminmanagement'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Admin Control
                        </NavLink>
                    )}

                    {/* Carrier Approval - Only SUPER_ADMIN and MAIN_ADMIN */}
                    {canViewMenuItem('carrierApproval') && (
                        <NavLink
                            to='/admin/carrier-approval'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Carrier Approval
                        </NavLink>
                    )}

                    {/* Driver Approval - Only SUPER_ADMIN and MAIN_ADMIN */}
                    {canViewMenuItem('driverApproval') && (
                        <NavLink
                            to='/admin/drivers-approval'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Driver Approval
                        </NavLink>
                    )}

                    {/* Pending Approvals - Only SUPER_ADMIN and MAIN_ADMIN */}
                    {canViewMenuItem('profileUpdate') && (
                        <NavLink
                            to='/admin/profile-update'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Pending Approvals
                        </NavLink>
                    )}

                    {/* Reported Issues - For support and admin */}
                    {canViewMenuItem('reportedIssues') && (
                        <NavLink
                            to='/admin/reported-issues'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Reported Issues
                        </NavLink>
                    )}

                    {/* Carriers - SUPER_ADMIN, MAIN_ADMIN, MARKETING_ADMIN */}
                    {canViewMenuItem('carriers') && (
                        <NavLink
                            to='/admin/carriers-listing'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Carriers
                        </NavLink>
                    )}

                    {/* Packages - SUPER_ADMIN, MAIN_ADMIN, MARKETING_ADMIN */}
                    {canViewMenuItem('packages') && (
                        <NavLink
                            to='/admin/packages'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Packages
                        </NavLink>
                    )}

                    {/* Special Package */}
                    {/* {canViewMenuItem('specialPackages') && (
                        <NavLink
                            to='/admin/special-package'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Special Package
                        </NavLink>
                    )} */}

                    {/* Special Package with Submenu */}
                    {canViewMenuItem('specialPackages') && (
                        <div>
                            <button
                                onClick={toggleSpecialPackagesMenu}
                                className={`w-full text-left py-3 px-4 rounded-lg font-medium flex items-center justify-between ${
                                    isSpecialPackagesOpen ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`}
                            >
                                <span>Special Package</span>
                                <svg
                                    className={`w-5 h-5 transition-transform duration-300 ${
                                        isSpecialPackagesOpen ? 'rotate-180' : ''
                                    }`}
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                    xmlns='http://www.w3.org/2000/svg'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M19 14l-7 7m0 0l-7-7m7 7V3'
                                    />
                                </svg>
                            </button>

                            {/* Submenu Items */}
                            {isSpecialPackagesOpen && (
                                <div className='ml-5 mt-2 space-y-1'>
                                    <NavLink
                                        to='/admin/special-package'
                                        className={({ isActive }) =>
                                            `block py-2 px-4 rounded-lg font-medium text-sm flex items-center ${
                                                isActive
                                                    ? 'bg-blue-700 text-white'
                                                    : 'hover:bg-blue-600 text-gray-300 transition duration-200'
                                            }`
                                        }
                                    >
                                        {/* <span className='mr-2'>•</span> */}
                                        Special Package List
                                    </NavLink>

                                    <NavLink
                                        to='/admin/special-package-subscribers'
                                        className={({ isActive }) =>
                                            `block py-2 px-4 rounded-lg font-medium text-sm flex items-center ${
                                                isActive
                                                    ? 'bg-blue-700 text-white'
                                                    : 'hover:bg-blue-600 text-gray-300 transition duration-200'
                                            }`
                                        }
                                    >
                                        {/* <span className='mr-2'>•</span> */}
                                        Subscribed Carriers
                                    </NavLink>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Drivers - Only SUPER_ADMIN and MAIN_ADMIN */}
                    {canViewMenuItem('drivers') && (
                        <NavLink
                            to='/admin/drivers-listing'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Drivers
                        </NavLink>
                    )}

                    {/* Demo Schedule - SUPER_ADMIN, MAIN_ADMIN, MARKETING_ADMIN */}
                    {canViewMenuItem('demoSchedule') && (
                        <NavLink
                            to='/admin/carrier-demo-bookings'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Demo Schedule
                        </NavLink>
                    )}

                    {/* Assign Drivers - Only SUPER_ADMIN and MAIN_ADMIN */}
                    {canViewMenuItem('driverAssign') && (
                        <NavLink
                            to='/admin/driver-assign'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Assign Drivers
                        </NavLink>
                    )}

                    {/* Add Drivers - Only SUPER_ADMIN and MAIN_ADMIN */}
                    {canViewMenuItem('addDriver') && (
                        <NavLink
                            to='/admin/add-driver'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Add Drivers
                        </NavLink>
                    )}

                    {/* Revenue & Billing - Only SUPER_ADMIN and MAIN_ADMIN */}
                    {canViewMenuItem('revenue') && (
                        <NavLink
                            to='/admin/revenue-report'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Revenue & Billing
                        </NavLink>
                    )}

                    {/* Reports - Only SUPER_ADMIN and MAIN_ADMIN */}
                    {canViewMenuItem('reports') && (
                        <NavLink
                            to='/admin/reports'
                            className={({ isActive }) =>
                                `block py-3 px-4 rounded-lg font-medium flex items-center ${
                                    isActive ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`
                            }
                        >
                            Reports
                        </NavLink>
                    )}
                </nav>

                {/* User Profile Section */}
                <div className='p-6 border-t border-gray-800'>
                    <div className='flex items-start flex-col'>
                        <div className='flex items-center mb-3'>
                            <div className='w-10 h-10 rounded-full bg-blue-800 flex items-center justify-center text-white font-bold'>
                                {getUserInitials()}
                            </div>
                            <div className='ml-3'>
                                <p className='font-medium text-white'>{capitalizeFirstLetter(user?.name) || 'Admin'}</p>
                                <p className='text-xs text-gray-400'>{formatRole(userRole) || 'User'}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className='w-full bg-blue-800 text-white font-semibold px-4 py-2 rounded hover:bg-blue-600 transition duration-200'
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </aside>

            {/* Mobile Sidebar */}
            <div
                className={`fixed top-0 right-0 h-full w-64 bg-black text-white flex flex-col z-50 overflow-y-auto transform transition-transform duration-300 ease-in-out ${
                    isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
                } md:hidden`}
            >
                <div className='p-6 text-3xl font-bold text-blue-700 border-b border-gray-800'>
                    <img src='../assets/FleetyBlue.png' alt='Fleety Logo' className='h-12' />
                </div>
                <nav className='flex-1 px-4 py-6 space-y-2'>
                    {/* Dashboard - Always Visible */}
                    <NavLink
                        to='/admin/dashboard'
                        className='bg-blue-600 text-white block py-3 px-4 rounded-lg font-medium flex items-center'
                        onClick={closeMobileMenu}
                    >
                        <svg
                            className='w-5 h-5 mr-3'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                            xmlns='http://www.w3.org/2000/svg'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                d='M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z'
                            />
                        </svg>
                        Dashboard
                    </NavLink>

                    {/* Admin Control - Conditional */}
                    {canViewMenuItem('adminControl') && (
                        <NavLink
                            to='/admin/adminmanagement'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Admin Control
                        </NavLink>
                    )}

                    {/* Carrier Approval - Conditional */}
                    {canViewMenuItem('carrierApproval') && (
                        <NavLink
                            to='/admin/carrier-approval'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Carrier Approval
                        </NavLink>
                    )}

                    {/* Driver Approval - Conditional */}
                    {canViewMenuItem('driverApproval') && (
                        <NavLink
                            to='/admin/drivers-approval'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Driver Approval
                        </NavLink>
                    )}

                    {/* Pending Approvals - Conditional */}
                    {canViewMenuItem('profileUpdate') && (
                        <NavLink
                            to='/admin/profile-update'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Pending Approvals
                        </NavLink>
                    )}

                    {/* Carriers - Conditional */}
                    {canViewMenuItem('carriers') && (
                        <NavLink
                            to='/admin/carriers-listing'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Carriers
                        </NavLink>
                    )}

                    {/* Packages - Conditional */}
                    {canViewMenuItem('packages') && (
                        <NavLink
                            to='/admin/packages'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Packages
                        </NavLink>
                    )}

                    {/* {canViewMenuItem('specialPackages') && (
                        <NavLink
                            to='/admin/special-package'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Special Package
                        </NavLink>
                    )} */}
                    {/* Special Package with Submenu */}
                    {canViewMenuItem('specialPackages') && (
                        <div>
                            <button
                                onClick={toggleSpecialPackagesMenu}
                                className={`w-full text-left py-3 px-4 rounded-lg font-medium flex items-center justify-between ${
                                    isSpecialPackagesOpen ? 'bg-blue-700' : 'hover:bg-blue-600 transition duration-200'
                                } text-white`}
                            >
                                <span>Special Package</span>
                                <svg
                                    className={`w-5 h-5 transition-transform duration-300 ${
                                        isSpecialPackagesOpen ? 'rotate-180' : ''
                                    }`}
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                    xmlns='http://www.w3.org/2000/svg'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M19 14l-7 7m0 0l-7-7m7 7V3'
                                    />
                                </svg>
                            </button>

                            {/* Submenu Items */}
                            {isSpecialPackagesOpen && (
                                <div className='ml-5 mt-2 space-y-1'>
                                    <NavLink
                                        to='/admin/special-package'
                                        className={({ isActive }) =>
                                            `block py-2 px-4 rounded-lg font-medium text-sm flex items-center ${
                                                isActive
                                                    ? 'bg-blue-700 text-white'
                                                    : 'hover:bg-blue-600 text-gray-300 transition duration-200'
                                            }`
                                        }
                                    >
                                        {/* <span className='mr-2'>•</span> */}
                                        Special Package List
                                    </NavLink>

                                    <NavLink
                                        to='/admin/special-package-subscribers'
                                        className={({ isActive }) =>
                                            `block py-2 px-4 rounded-lg font-medium text-sm flex items-center ${
                                                isActive
                                                    ? 'bg-blue-700 text-white'
                                                    : 'hover:bg-blue-600 text-gray-300 transition duration-200'
                                            }`
                                        }
                                    >
                                        {/* <span className='mr-2'>•</span> */}
                                        Subscribed Carriers
                                    </NavLink>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Drivers - Conditional */}
                    {canViewMenuItem('drivers') && (
                        <NavLink
                            to='/admin/drivers-listing'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Drivers
                        </NavLink>
                    )}

                    {/* Demo Schedule - Conditional */}
                    {canViewMenuItem('demoSchedule') && (
                        <NavLink
                            to='/admin/carrier-demo-bookings'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Demo Schedule
                        </NavLink>
                    )}

                    {/* Assign Drivers - Conditional */}
                    {canViewMenuItem('driverAssign') && (
                        <NavLink
                            to='/admin/driver-assign'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Assign Drivers
                        </NavLink>
                    )}

                    {/* Add Drivers - Conditional */}
                    {canViewMenuItem('addDriver') && (
                        <NavLink
                            to='/admin/add-driver'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Add Drivers
                        </NavLink>
                    )}

                    {/* Revenue & Billing - Conditional */}
                    {canViewMenuItem('revenue') && (
                        <NavLink
                            to='/admin/revenue-report'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Revenue & Billing
                        </NavLink>
                    )}

                    {/* Reports - Conditional */}
                    {canViewMenuItem('reports') && (
                        <NavLink
                            to='/admin/reports'
                            className='block py-3 px-4 rounded-lg font-medium flex items-center hover:bg-blue-600 transition duration-200'
                            onClick={closeMobileMenu}
                        >
                            Reports
                        </NavLink>
                    )}
                </nav>

                {/* User Profile Section */}
                <div className='p-6 border-t border-gray-800'>
                    <div className='flex items-start flex-col'>
                        <div className='flex items-center mb-3'>
                            <div className='w-10 h-10 rounded-full bg-blue-800 flex items-center justify-center text-white font-bold'>
                                {getUserInitials()}
                            </div>
                            <div className='ml-3'>
                                <p className='font-medium text-white'>{capitalizeFirstLetter(user?.name) || 'Admin'}</p>
                                <p className='text-xs text-gray-400'>{formatRole(userRole) || 'User'}</p>
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                handleLogout();
                                closeMobileMenu();
                            }}
                            className='w-full bg-blue-800 text-white font-semibold px-4 py-2 rounded hover:bg-blue-600 transition duration-200'
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Overlay for Mobile Menu */}
            {isMobileMenuOpen && (
                <div className='fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden' onClick={closeMobileMenu}></div>
            )}
        </>
    );
};

export default AdminSideMenu;
